import * as React from 'react'
import { Check } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { cn } from '@/lib/utils'

export interface MultiSelectOption {
  value: string
  label: string
  /** Optional adornment rendered before the label (e.g. avatar, color swatch). */
  icon?: React.ReactNode
  /** Optional secondary text shown right-aligned. */
  hint?: string
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  values: string[]
  onChange: (next: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyMessage?: string
  /** Width applied to the popover (Tailwind class). Defaults to w-[260px]. */
  contentWidthClassName?: string
  /** Custom trigger renderer. Receives the selected options and a "click to open" handler. */
  renderTrigger?: (selected: MultiSelectOption[]) => React.ReactNode
  disabled?: boolean
  /** Class for the default trigger button. */
  triggerClassName?: string
  align?: 'start' | 'center' | 'end'
}

/**
 * MultiSelect — standardized multi-select dropdown built on Popover + cmdk Command.
 *
 * Drop-in replacement for ad-hoc popover-with-checkboxes patterns. Scrolls correctly
 * when nested inside a Dialog (PopoverContent already stops wheel propagation), supports
 * search and keyboard nav out of the box, and accepts a custom trigger renderer so each
 * caller can show its own chip stack / count / icon row.
 */
export function MultiSelect({
  options,
  values,
  onChange,
  placeholder = '+ Add',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No results.',
  contentWidthClassName = 'w-[260px]',
  renderTrigger,
  disabled,
  triggerClassName,
  align = 'start',
}: MultiSelectProps) {
  const selected = React.useMemo(
    () => options.filter((o) => values.includes(o.value)),
    [options, values]
  )

  function toggle(value: string) {
    if (values.includes(value)) onChange(values.filter((v) => v !== value))
    else onChange([...values, value])
  }

  return (
    <Popover>
      <PopoverTrigger asChild disabled={disabled}>
        {renderTrigger ? (
          <button type="button" disabled={disabled} className="text-left">
            {renderTrigger(selected)}
          </button>
        ) : (
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'flex min-h-9 w-full items-center gap-1 rounded-md border px-2 py-1.5 text-left text-sm hover:bg-muted/30 disabled:cursor-not-allowed disabled:opacity-50',
              triggerClassName
            )}
          >
            {selected.length === 0 ? (
              <span className="text-xs text-muted-foreground">{placeholder}</span>
            ) : (
              <span className="text-xs">
                {selected.map((o) => o.label).join(', ')}
              </span>
            )}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent className={cn('p-0', contentWidthClassName)} align={align}>
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const checked = values.includes(opt.value)
                return (
                  <CommandItem
                    key={opt.value}
                    value={opt.label}
                    onSelect={() => toggle(opt.value)}
                    className="flex items-center gap-2"
                  >
                    {opt.icon}
                    <span className="flex-1 truncate">{opt.label}</span>
                    {opt.hint && (
                      <span className="text-xs text-muted-foreground">{opt.hint}</span>
                    )}
                    {checked && <Check className="h-4 w-4 text-primary" />}
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
